import { useRef } from 'react';

/**
 * Navigation par BALAYAGE tactile horizontal (mobile) : swipe vers la GAUCHE →
 * `onNext`, vers la DROITE → `onPrev` (convention usuelle). Repose sur les Pointer
 * Events (comme le pan de `ZoomableImage`).
 *
 * Garanties :
 * - N'agit QUE si `enabled` (ex. image NON zoomée — au zoom, le glisser sert au
 *   panoramique, on ne navigue pas).
 * - Détecte l'axe DOMINANT : un balayage majoritairement VERTICAL est ignoré
 *   (défilement/geste vertical), seul un mouvement franchement horizontal navigue.
 * - Applique un SEUIL de distance horizontale (`SWIPE_THRESHOLD_PX`) : un simple
 *   frémissement / tap ne déclenche jamais de navigation → n'entre pas en conflit
 *   avec le « clic sur le fond ferme » ni avec un tap sur les contrôles.
 * - Respecte les bornes : jamais `onNext`/`onPrev` au-delà de `canNext`/`canPrev`.
 *
 * Retourne des handlers Pointer à épandre sur l'élément média (image ou vidéo).
 */

/** Déplacement horizontal (px) minimal pour qu'un geste compte comme un swipe. */
export const SWIPE_THRESHOLD_PX = 50;

export interface SwipeNavOptions {
  enabled: boolean;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}

export interface SwipeNavHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: () => void;
}

export function useSwipeNav({
  enabled,
  canPrev,
  canNext,
  onPrev,
  onNext,
}: SwipeNavOptions): SwipeNavHandlers {
  // Point de départ du geste courant (null = pas de geste tactile suivi).
  const startRef = useRef<{ x: number; y: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent): void => {
    // Seul le tactile navigue par swipe (la souris a molette/flèches/pan) ; on
    // ignore aussi les clics droits.
    if (e.pointerType !== 'touch' || e.button !== 0) {
      startRef.current = null;
      return;
    }
    startRef.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerUp = (e: React.PointerEvent): void => {
    const start = startRef.current;
    startRef.current = null;
    if (!enabled || !start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    // Axe DOMINANT horizontal + seuil de distance.
    if (Math.abs(dx) <= Math.abs(dy)) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    // dx < 0 : doigt vers la gauche → image SUIVANTE ; dx > 0 : vers la droite → précédente.
    if (dx < 0) {
      if (canNext) onNext();
    } else {
      if (canPrev) onPrev();
    }
  };

  const onPointerCancel = (): void => {
    startRef.current = null;
  };

  return { onPointerDown, onPointerUp, onPointerCancel };
}
